/**
 * 飞书错误上报 - 错误监听器
 */

import type { FeishuErrorReporterConfig } from './types';
import { DEFAULT_API_ERROR_KEYWORDS } from './constants';
import { formatErrorReason } from './formatters';
import { addErrorToQueue } from './queue';

// 保存原始函数引用，用于销毁时恢复
let originalOnError: OnErrorEventHandler | null = null;
let originalConsoleError: ((...args: any[]) => void) | null = null;
let unhandledRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;
let resourceErrorHandler: ((event: Event) => void) | null = null;

/**
 * 需要忽略的 Promise rejection 错误模式
 * 这些错误是无害的，不需要上报
 */
const IGNORED_REJECTION_PATTERNS = [
  'Permission was denied', // 视频自动播放被浏览器阻止
  'play() failed', // 自动播放失败
  "user didn't interact", // 用户未与文档交互导致的自动播放失败
  'The operation was aborted', // 用户离开页面时请求被取消
  'The requesting page is not visible', // 页面在后台时尝试播放被阻止
  'not allowed by the user agent', // 浏览器阻止自动播放（如 libpag 动画）
  'Load failed', // 网络波动导致的加载失败
  'Network Error', // 网络错误（弱网环境）
  'Failed to fetch', // fetch 请求失败（网络问题）
  'NetworkError', // 网络错误
  '已取消', // 用户主动取消操作
  'this.flv', // xgplayer 播放器内部错误（flv 对象已销毁，Safari）
  '_transferCost', // xgplayer 播放器内部错误（flv 对象已销毁，Chrome）
  'bad network response', // 播放器网络响应错误（用户网络问题）
  // 火山 SDK 相关错误（全部屏蔽）
  'livesaas', // 火山直播 SDK 相关错误
  'volcengine', // 火山引擎相关错误
  'ByteLive', // 火山直播 SDK
];

/**
 * 需要忽略的堆栈模式（检查 stack 而非 message）
 * 这些是第三方库内部错误，不需要上报
 */
const IGNORED_STACK_PATTERNS = [
  'xgplayer-flv', // xgplayer FLV 播放器内部错误
  'xgplayer', // xgplayer 播放器内部错误
  '_onSBUpdateError', // SourceBuffer 更新错误
  'livesaas-client', // 火山引擎直播 SDK 内部错误（如 videoWidth 读取失败）
  // 火山 SDK 相关堆栈（全部屏蔽）
  'livesaas', // 火山直播 SDK（包含所有 livesaas 变体）
  'volccdn.com', // 从火山 CDN 加载的脚本
  'volcdn.com', // 从火山 CDN 备用域名加载的脚本
  'byted-static.com', // 从字节静态资源加载的脚本
  'volcengine', // 火山引擎 SDK
  'ByteLive', // 火山直播 SDK
];

/**
 * 需要忽略的 JS 错误模式
 * 这些是播放器相关的无害错误，不需要上报
 */
const IGNORED_JS_ERROR_PATTERNS = [
  'MediaSource attachment is closing', // MediaSource 关闭时的竞态条件错误
  'SourceBuffer', // SourceBuffer 相关错误（通常是播放器内部错误）
  'Script error', // 跨域脚本错误，无详细信息，无法调试
  'ResizeObserver loop', // ResizeObserver 循环警告，浏览器保护机制，无害
  'DataCloneError', // Worker postMessage 内存不足，火山 SDK 长连接问题，用户设备问题
  'InvalidStateError', // 播放器/SDK 内部状态错误，操作已销毁的对象
  "Can't find variable: WeakRef", // 旧版浏览器不支持 WeakRef（ES2021），如 iOS Safari 14.4 以下
  'Uncaught ReferenceError: WeakRef is not defined',
  'LIDNotifyId', // 外部注入脚本（原生桥/浏览器扩展）引用的未定义全局，堆栈为 ":1:1"，非本站代码
  // 火山 SDK 相关 JS 错误（全部屏蔽）
  'livesaas', // 火山直播 SDK JS 错误
  'volcengine', // 火山引擎 SDK JS 错误
  'ByteLive', // 火山直播 SDK JS 错误
  "Can't find variable: WeakRef",
];

/**
 * 检查是否为 API 错误
 */
function isApiError(message: string, config: FeishuErrorReporterConfig | null): boolean {
  const keywords = config?.apiErrorKeywords || DEFAULT_API_ERROR_KEYWORDS;
  return keywords.some((keyword) => message.includes(keyword));
}

/**
 * 设置 JavaScript 错误监听器
 */
export function setupJSErrorListener(config: FeishuErrorReporterConfig): void {
  // 监听全局错误（window.onerror）
  originalOnError = window.onerror;

  window.onerror = function (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error,
  ): boolean {
    const errorMessage = typeof message === 'string' ? message : error?.message || String(message);
    const stack = error?.stack;

    // 过滤掉已知的无害错误（如播放器内部错误）
    const shouldIgnoreByMessage = IGNORED_JS_ERROR_PATTERNS.some((pattern) => errorMessage.includes(pattern));
    if (shouldIgnoreByMessage) {
      // 调用原始的onerror处理器
      if (originalOnError) {
        return originalOnError.call(window, message, source, lineno, colno, error);
      }
      return false;
    }

    // 过滤掉第三方库内部错误（检查堆栈）
    const shouldIgnoreByStack = stack && IGNORED_STACK_PATTERNS.some((pattern) => stack.includes(pattern));
    if (shouldIgnoreByStack) {
      if (originalOnError) {
        return originalOnError.call(window, message, source, lineno, colno, error);
      }
      return false;
    }

    if (!isApiError(errorMessage, config)) {
      addErrorToQueue(
        {
          message: errorMessage,
          stack: error?.stack,
          filename: source,
          lineno,
          colno,
          errorType: 'js-error',
        },
        config,
      );
    }

    // 调用原始的onerror处理器
    if (originalOnError) {
      return originalOnError.call(window, message, source, lineno, colno, error);
    }

    return false;
  };

  // 可选：监听console.error（默认关闭，避免噪音）
  if (config?.captureConsoleError) {
    originalConsoleError = console.error;
    console.error = function (...args: any[]) {
      const errorMessage = args.join(' ');

      if (!isApiError(errorMessage, config)) {
        addErrorToQueue(
          {
            message: errorMessage,
            errorType: 'js-error',
          },
          config,
        );
      }

      // 调用原始的console.error
      originalConsoleError?.apply(console, args);
    };
  }
}

/**
 * 设置 Promise rejection 监听器
 */
export function setupPromiseRejectionListener(config: FeishuErrorReporterConfig): void {
  // 监听未捕获的Promise rejection
  unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
    // 格式化错误原因
    const formattedReason = formatErrorReason(event.reason);

    // 过滤掉已知的无害错误（检查消息）
    const shouldIgnoreByMessage = IGNORED_REJECTION_PATTERNS.some((pattern) => formattedReason.includes(pattern));
    if (shouldIgnoreByMessage) return;

    // 获取堆栈信息
    const stack =
      event.reason && typeof event.reason === 'object' && 'stack' in event.reason
        ? (event.reason as Error).stack
        : undefined;

    // 过滤掉已知的无害错误（检查堆栈，如第三方库内部错误）
    const shouldIgnoreByStack = stack && IGNORED_STACK_PATTERNS.some((pattern) => stack.includes(pattern));
    if (shouldIgnoreByStack) return;

    if (!isApiError(formattedReason, config)) {
      addErrorToQueue(
        {
          message: `未捕获的Promise rejection: ${formattedReason}`,
          stack,
          errorType: 'promise-rejection',
        },
        config,
      );
    }
  };

  window.addEventListener('unhandledrejection', unhandledRejectionHandler);
}

/**
 * 需要忽略的资源加载错误类型
 * 这些资源加载失败通常是正常情况，不需要上报
 */
const IGNORED_RESOURCE_TYPES = [
  'video', // 直播场景下视频加载失败是常见情况（流切换、网络波动等）
  'audio', // 音频同理
  'xg-video', // xgplayer 播放器的视频元素
];

/**
 * 需要忽略的资源 URL 模式
 * 第三方 CDN 资源加载失败通常是用户网络问题，不需要上报
 */
const IGNORED_RESOURCE_URL_PATTERNS = [
  // 火山引擎/字节跳动 CDN（全部屏蔽）
  'volccdn.com', // 火山引擎 CDN
  'volcdn.com', // 火山引擎 CDN 备用域名
  'volcengine.com', // 火山引擎域名
  'byted-static.com', // 字节跳动静态资源
  'bytedance.com', // 字节跳动域名
  'bytegoofy.com', // 字节跳动 CDN
  'douyinstatic.com', // 抖音静态资源 CDN
  // 直播流 CDN
  'zebracdn.com', // 直播流 CDN
  // 其他第三方 CDN
  'unpkg.com', // unpkg CDN
  'cdnjs.cloudflare.com', // Cloudflare CDN
  'cdn.jsdelivr.net', // jsDelivr CDN
];

/**
 * 设置资源加载错误监听器
 */
export function setupResourceErrorListener(config: FeishuErrorReporterConfig): void {
  // 监听资源加载错误（图片、脚本、样式等）
  resourceErrorHandler = (event: Event) => {
    // 只处理资源加载错误，不处理JavaScript错误（由window.onerror处理）
    if (event.target && event.target !== window) {
      const target = event.target as HTMLElement;
      const resourceType = target.tagName?.toLowerCase();

      // 过滤掉不需要上报的资源类型
      if (IGNORED_RESOURCE_TYPES.includes(resourceType)) {
        return;
      }

      const resourceUrl =
        (target as HTMLImageElement).src ||
        (target as HTMLLinkElement).href ||
        (target as HTMLScriptElement).src ||
        (target as HTMLVideoElement).currentSrc ||
        (target as HTMLVideoElement).src ||
        'unknown';

      // 过滤掉第三方 CDN 资源加载错误
      const shouldIgnoreByUrl = IGNORED_RESOURCE_URL_PATTERNS.some((pattern) => resourceUrl.includes(pattern));
      if (shouldIgnoreByUrl) {
        return;
      }

      addErrorToQueue(
        {
          message: `资源加载失败: ${resourceType} - ${resourceUrl}`,
          filename: resourceUrl,
          errorType: 'resource-error',
        },
        config,
      );
    }
  };

  window.addEventListener('error', resourceErrorHandler, true); // 使用捕获阶段
}

/**
 * 移除所有错误监听器
 */
export function removeAllListeners(): void {
  // 恢复 window.onerror
  if (originalOnError !== undefined) {
    window.onerror = originalOnError;
    originalOnError = null;
  }

  // 恢复 console.error
  if (originalConsoleError) {
    console.error = originalConsoleError;
    originalConsoleError = null;
  }

  // 移除 unhandledrejection 监听器
  if (unhandledRejectionHandler) {
    window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
    unhandledRejectionHandler = null;
  }

  // 移除资源错误监听器
  if (resourceErrorHandler) {
    window.removeEventListener('error', resourceErrorHandler, true);
    resourceErrorHandler = null;
  }
}
